<template>
  <div class="details">
    <div class="card br-2">
      <div class="p-10">
        <h6 class="fw-bold">TELEHEALTH: {{ telehealthVisits.length }}</h6>
        <div v-show="telehealthVisits && telehealthVisits.length > 0">
          <div>
            <a href class="col-1 p-0 make-link" @click.prevent="expandAll()">Expand all</a>
            <span class="link_style"></span>
            <a href class="col-1 p-0 make-link" @click.prevent="collapseAll()">Collapse all</a>
          </div>
          <div v-if="studyDetails && studyDetails.countrySpecificTelehealthFeature" class="mt-6 country-specific-status">
            <div v-if="studyDetails.countrySpecificTelehealthFeature.telehealthEnableCountryList.length > 0">
              <span class="min-width">Enabled in:</span><span>{{ studyDetails.countrySpecificTelehealthFeature.telehealthEnableCountryList.join(", ") }}</span>
            </div>
            <div v-if="studyDetails.countrySpecificTelehealthFeature.telehealthDisableCountryList.length > 0">
              <span class="min-width">Disabled in:</span><span>{{ studyDetails.countrySpecificTelehealthFeature.telehealthDisableCountryList.join(", ") }}</span>
            </div>
          </div>

          <div v-if="studyDetails.visitLevelRecordingEnabled || studyDetails.studyLevelRecordingEnabled" class="country-specific-status">
            <span class="min-width">Telehealth recording:</span>
            <span v-if="studyDetails.visitLevelRecordingEnabled">Allow and configure recording features at each visit</span>
            <span v-if="studyDetails.studyLevelRecordingEnabled">Allow recording features for all telehealth visits</span>
          </div>
          <div v-if="studyDetails.visitLevelRecordingEnabled || studyDetails.studyLevelRecordingEnabled" class="country-specific-status">
            <span v-if="thRecordingAllowedInCountries.length > 0" class="min-width"
              >Recording allowed in the following countries:
              <span v-for="(countryAllowed, index) in thRecordingAllowedInCountries" :key="index">{{ countryAllowed.name }}<span v-if="index + 1 < thRecordingAllowedInCountries.length">, </span></span>
            </span>
            <span v-else class="min-width">Recording allowed in all countries</span>
          </div>

          <div class="pt-8" v-for="(visit, index) in telehealthVisits" :key="`telehealth-visit-${index}`">
            <div>
              <b-row>
                <b-col cols="3" class="p-0">
                  <i class="fa fa-plus-circle pr-6" v-if="!visitCollapse[visit.taskId]" @click="expandVisit(visit.taskId)"></i>
                  <i class="fa fa-minus-circle pr-6" v-if="visitCollapse[visit.taskId]" @click="collapseVisit(visit.taskId)"></i>
                  {{ visit.visitName }}
                </b-col>
                <b-col cols="4" v-if="visit">
                  {{ getActivityMode(index) }}
                </b-col>
                <b-col cols="4" v-if="visit.milestones && visit.milestones.length > 0">
                  Milestones:
                  <span v-for="(milestones, index) in visit.milestones" :key="index">
                    {{ milestones }}
                    <span v-if="index + 1 < visit.milestones.length">,</span>
                  </span>
                </b-col>
                <b-col cols="4" v-if="visit.frequency && visit.frequency.title">Frequency: {{ visit.frequency.title }}</b-col>
              </b-row>
            </div>
            <div v-if="visitCollapse[visit.taskId]">
              <div class="card inner-card br-2">
                <div class="p-10">
                  <b-row>
                    <b-col class="pl-0 pb-10" v-if="visit.milestones && visit.milestones.length > 0">
                      <div class="header">To be completed at milestones</div>
                      <div class="content">
                        <span v-for="(milestones, index) in visit.milestones" :key="index">
                          {{ milestones }}
                          <span v-if="index + 1 < visit.milestones.length">,</span>
                        </span>
                      </div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-if="visit.frequency && visit.frequency.title">
                      <div class="header">Frequency</div>
                      <div class="content">{{ visit.frequency.title }}</div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-if="visit.completionWindow && visit.type != 'milestone'">
                      <div class="header">Completion window</div>
                      <div class="content">{{ visit.completionWindow }} days</div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-if="visit.completionWindows && Object.keys(visit.completionWindows).length != 0 && visit.type === 'milestone'">
                      <div class="header">Completion windows</div>
                      <div class="content">
                        {{ Object.values(visit.completionWindows).join(", ") }}
                      </div>
                    </b-col>
                  </b-row>
                  <b-row class="content pb-10" v-if="visit.isActivityOptional">Activity is optional and will be excluded from the compliance report.</b-row>
                  <b-row>
                    <b-col class="pl-0 pb-10">
                      <div class="header">
                        Days in advance participants can schedule their Telehealth visit
                      </div>
                      <div class="content">{{ visit.advanceScheduleDays }}</div>
                    </b-col>
                    <b-col v-if="visit.extendAvailability" class="pl-0 pb-10">
                      <div class="header">Extended completion window</div>
                      <div class="content">{{ visit.extendWindow }} days</div>
                    </b-col>
                  </b-row>
                  <b-row v-if="visit.duration && (visit.duration[0].startDay || visit.duration[0].endDay)">
                    <b-col class="pl-0 pb-10">
                      <div class="header">
                        Selected time period this survey should appear
                      </div>
                      <div class="content">Start Day: {{ visit.duration[0].startDay }}</div>
                      <div class="content" v-if="visit.duration[0].endDay">End Day: {{ visit.duration[0].endDay }}</div>
                      <div class="content" v-else>
                        Available until the end of participant's study duration
                      </div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col class="pl-0 pb-10">
                      <div class="header">Visit Type</div>
                      <div class="content">
                        {{ visit.visitType == "bothVisitType" ? "Both Visit Types" : showVisitDesc(visit.visitType) }}
                      </div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-if="studyDetails.visitLevelRecordingEnabled && visit.allowRecording && isTelehealthRecordingFeature">
                      <div class="header">Telehealth recording</div>
                      <div class="content" v-if="visit.allowRecording">
                        Allow and configure recording features at each visit
                      </div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-else-if="studyDetails.studyLevelRecordingEnabled && visit.allowRecording && isTelehealthRecordingFeature">
                      <div class="header">Telehealth recording</div>
                      <div class="content" v-if="studyDetails.studyLevelRecordingEnabled">
                        Allow recording features for all telehealth visits
                      </div>
                    </b-col>
                    <b-col class="pl-0 pb-10" v-else-if="!visit.allowRecording">
                      <div class="header">Telehealth recording</div>
                      <div class="content"></div>
                    </b-col>
                  </b-row>
                </div>
                <b-row class="pb-10 pl-10" v-if="visit.appNotification && visit.appNotification.length > 0">
                  <b-col cols="12" class="header p-0 mb-5">Reminders</b-col>
                  <div v-for="(appNotification, index) in visit.appNotification" :key="index" class="mobile-notifications">
                    <b-col cols="12" class="p-0 mb-10">
                      <div class="header">Event</div>
                      <div class="content">{{ appNotification.eventType }}</div>
                    </b-col>
                    <b-col cols="12" class="p-0 mb-10">
                      <b-row class="p-0">
                        <b-col class="p-0" cols="5" v-if="appNotification.message">
                          <div class="header">Message</div>
                          <div class="content">
                            {{ appNotification.message }}
                          </div>
                        </b-col>
                        <b-col class="p-0" cols="3" v-if="appNotification.time">
                          <div class="header">Time</div>
                          <div class="content">{{ appNotification.time }}</div>
                        </b-col>
                      </b-row>
                    </b-col>
                    <b-col cols="12" class="p-0 mb-10">
                      <b-row class="p-0">
                        <b-col class="p-0" cols="5">
                          <div class="header">Occurrence: {{ appNotification.occuranceLength }}</div>
                          <div class="content">
                            {{ appNotification.occurances }}
                          </div>
                        </b-col>
                        <b-col class="p-0" cols="7">
                          <div class="header">Notification type</div>
                          <div class="content">
                            <span>Mobile</span>
                            <span v-if="appNotification.emailToggle">, Email</span>
                            <span v-if="appNotification.smsToggle">, SMS Text</span>
                          </div>
                        </b-col>
                      </b-row>
                    </b-col>
                  </div>
                </b-row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* 
IMPORTANT
Any changes made in this file should also be made in the telehealth-details.vue which is inside the study-details-pdf folder
 */
export default {
  name: "telehealth-details",
  data() {
    return {
      telehealthVisits: [],
      visitCollapse: {},
      eventTypeoptions: {
        1: "When it's time to schedule the appointment",
        2: "When its time for the appointment"
      },
      expandRecordingSection: false
    };
  },
  components: {},
  props: {
    visits: {
      type: Array
    },
    thRecordingAllowedInCountries: {
      type: Array
    },
    studyDetails: {
      type: Object,
      required: true
    },
    isTelehealthRecordingFeature: {
      type: Boolean
    }
  },
  methods: {
    expandVisit(id) {
      this.visitCollapse[id] = true;
      this.$forceUpdate();
    },
    collapseVisit(id) {
      this.visitCollapse[id] = false;
      this.$forceUpdate();
    },
    expandAll() {
      this.visits.forEach(visit => {
        this.visitCollapse[visit.taskId] = true;
      });
      this.$forceUpdate();
      this.expandRecordingSection = true;
    },
    collapseAll() {
      this.visits.forEach(visit => {
        this.visitCollapse[visit.taskId] = false;
      });
      this.$forceUpdate();
      this.expandRecordingSection = false;
    },
    getOccuranceText(value, eventType, delayType) {
      let stringText = "";
      if (eventType == 2) {
        if (value == 0) {
          stringText = "At time of event";
        } else {
          if (value == 60) {
            stringText = `1 hour ${delayType} the appointment time`;
          } else if (value == 120) {
            stringText = `2 hours ${delayType} the appointment time`;
          } else if (value == 1440) {
            stringText = `1 day ${delayType} the appointment time`;
          } else if (value == 2880) {
            stringText = `2 days ${delayType} the appointment time`;
          } else if (value == 7200) {
            stringText = `5 days ${delayType} the appointment time`;
          } else if (value == 10080) {
            stringText = `7 days ${delayType} the appointment time`;
          } else if (value == 14400) {
            stringText = `10 days ${delayType} the appointment time`;
          } else {
            stringText = value + ` min ${delayType} the appointment time`;
          }
          stringText += delayType == "after" ? "(if call has not begun)" : "";
        }
      } else {
        if (value == 0) {
          stringText = "At time of event";
        } else if (value < 0) {
          stringText = Math.abs(value) + " day(s) in advance";
        } else {
          stringText = value + " day(s) after (if not complete)";
        }
      }
      return stringText;
    },
    getOccuranceString(reminders, eventType) {
      let stringText = "";
      reminders.forEach(reminderObj => {
        let occuranceTextFor = reminderObj.day;
        if (eventType == 2) {
          const time = reminderObj.time;
          const timeArr = time.split(" ");
          occuranceTextFor = timeArr[0];
        }

        stringText = stringText + ", " + this.getOccuranceText(occuranceTextFor, eventType, reminderObj.delayType);
      });

      stringText = stringText.substr(1);
      return stringText;
    },
    getActivityMode(index) {
      const visit = this.telehealthVisits[index];
      if (visit.activitySchedulingMode) {
        if (visit.activitySchedulingMode === "schedule") {
          return "Scheduled";
        } else if (visit.activitySchedulingMode === "both") {
          return "Scheduled and On-demand";
        } else {
          return "On-demand";
        }
      } else {
        return "Scheduled";
      }
    },
    getSiteScheduleOccuranceString(reminders) {
      let stringText = "";
      reminders.forEach(reminderObj => {
        const occuranceDelayType = reminderObj.when === "beforeApptTime" ? "before" : "after";
        stringText = stringText + ", " + this.getSiteScheduleOccuranceText(reminderObj.timeMinutes, occuranceDelayType);
      });
      stringText = stringText.substr(1);
      return stringText;
    },
    getSiteScheduleOccuranceText(value, delayType) {
      let stringText = "";
      if (value == 0) {
        stringText = "At time of event";
      } else {
        if (value == -60) {
          stringText = `1 hour ${delayType} the appointment time`;
        } else if (value == -120) {
          stringText = `2 hours ${delayType} the appointment time`;
        } else if (value == -1440) {
          stringText = `1 day ${delayType} the appointment time`;
        } else if (value == -2880) {
          stringText = `2 days ${delayType} the appointment time`;
        } else {
          stringText = delayType === "after" ? value + ` min ${delayType} the appointment time` : Math.abs(value) + ` min ${delayType} the appointment time`;
        }
        stringText += delayType == "after" ? "(if call has not begun)" : "";
      }
      return stringText;
    },
    showVisitDesc(visitType) {
      return visitType
        .replace("Telehealth", "Video Call")
        .replace("In-person", "Office Visit")
        .replace("Homevisit", "Home Visit")
        .replace("Phonecall", "Phone Call");
    }
  },
  watch: {
    visits() {
      if (this.visits) {
        this.visits.forEach((visit, index) => {
          this.visitCollapse[visit.taskId] = false;
          const notifications = [];
          if (visit && visit.callReminders && visit.callReminders.length > 0) {
            const eventType2Notifications = {};
            eventType2Notifications.eventType = this.eventTypeoptions[2];
            eventType2Notifications.message = visit.callReminders[0].message;
            eventType2Notifications.emailToggle = visit.callReminders[0].emailToggle;
            eventType2Notifications.smsToggle = visit.callReminders[0].smsToggle;
            eventType2Notifications.occuranceLength = visit.callReminders.length;
            eventType2Notifications.occurances = this.getOccuranceString(visit.callReminders, 2);
            notifications.push(eventType2Notifications);
          }
          if (visit && visit.reminders && visit.reminders.length > 0) {
            const eventType1Notifications = {};
            eventType1Notifications.eventType = this.eventTypeoptions[1];
            eventType1Notifications.time = visit.reminders[0].time;
            eventType1Notifications.message = visit.reminders[0].message;
            eventType1Notifications.occuranceLength = visit.reminders.length;
            eventType1Notifications.emailToggle = visit.reminders[0].emailToggle;
            eventType1Notifications.smsToggle = visit.reminders[0].smsToggle;
            eventType1Notifications.occurances = this.getOccuranceString(visit.reminders, 1);
            notifications.push(eventType1Notifications);
          }
          if (visit && visit.siteTaskCallReminders && visit.siteTaskCallReminders.length > 0) {
            const eventType2Notifications = {};
            eventType2Notifications.eventType = this.eventTypeoptions[2];
            eventType2Notifications.message = visit.siteTaskCallReminders[0].message;
            eventType2Notifications.emailToggle = visit.siteTaskCallReminders[0].emailToggle;
            eventType2Notifications.smsToggle = visit.siteTaskCallReminders[0].smsToggle;
            eventType2Notifications.occuranceLength = visit.siteTaskCallReminders[0].occur.length;
            eventType2Notifications.occurances = this.getSiteScheduleOccuranceString(visit.siteTaskCallReminders[0].occur);
            notifications.push(eventType2Notifications);
          }

          this.telehealthVisits[index] = visit;
          this.telehealthVisits[index].appNotification = notifications;
        });
      }
    }
  }
};
</script>

<style scoped>
.link_style {
  border-right: 1px solid #959fa4;
  margin: 0px 5px 0px 2px !important;
}
.inner-card {
  border: none;
  margin: 5px 20px;
  border-radius: 3px !important;
  background-color: #d3dfe4;
}
.content {
  font-size: 12px;
  color: #666669;
}
.rec-header {
  font-size: 14px;
  font-weight: bold;
  color: #666669;
  padding-bottom: 9px;
}
.header {
  font-size: 12px;
  font-weight: bold;
  color: #666669;
}
.mobile-notifications {
  border-bottom: 1px solid #fff;
  margin-bottom: 10px;
  width: 100%;
  margin-right: 10px;
}

.mobile-notifications:last-of-type {
  border: 0;
}

.country-specific-status {
  color: #2d3752;
}

.min-width {
  display: inline-block;
  min-width: 161px;
}
.make-link {
  color: #1e8fe1 !important;
}
</style>
